import type { MouseEvent } from 'react';
import { useState } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import { MoreVert } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';
import { IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

interface IconWithDropdownProps<T> {
  values: {
    icon: SvgIconComponent;
    onClick: (returnValue: T) => void;
    label: string;
    id: string | number;
    disabled?: boolean;
    dataTestId?: string;
    iconDataTestId?: string;
    textDataTestId?: string;
    menuDataTestId?: string;
    menuTriggerDataTestId?: string;
  }[];
  helperText?: string;
  returnValue: T;
  sx?: SxProps<Theme>;
  dataTestId?: string;
}

export const IconWithDropdown = <T,>({ values, helperText, returnValue, sx, dataTestId }: IconWithDropdownProps<T>) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuDataTestId = values.find((value) => value.menuDataTestId)?.menuDataTestId;
  const menuTriggerDataTestId = values.find((value) => value.menuTriggerDataTestId)?.menuTriggerDataTestId;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={handleClick} sx={sx} data-testid={menuTriggerDataTestId ?? dataTestId ?? 'icon-with-dropdown-button'}>
        <Tooltip title={helperText || t('common.settings')}>
          <MoreVert />
        </Tooltip>
      </IconButton>
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        data-testid={menuDataTestId}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(43,54,116,0.12), 0 1px 4px rgba(43,54,116,0.08)',
              border: '1px solid rgba(43,54,116,0.08)',
              minWidth: '160px',
              mt: '4px',
            },
          },
        }}
        MenuListProps={{ sx: { py: '6px' } }}>
        {values.map((value, i) => {
          const Icon = value.icon;
          return (
            <MenuItem
              key={`${value.id}-${String(i)}`}
              disabled={value.disabled}
              onClick={() => {
                value.onClick(returnValue);
              }}
              data-testid={value.dataTestId ?? (dataTestId ? `${dataTestId}-menu-item-${value.id}` : `icon-with-dropdown-menu-item-${value.id}`)}
              sx={{
                px: '14px',
                py: '8px',
                fontSize: '13px',
                fontWeight: 500,
                '&:hover': { backgroundColor: 'rgba(39,72,251,0.06)' },
              }}>
              <Icon data-testid={value.iconDataTestId} sx={{ fontSize: 18, mr: '10px', flexShrink: 0 }} />
              <Typography data-testid={value.textDataTestId} sx={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 1.4 }}>
                {value.label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
